import { useMemo, useState } from "react";
import { get as lodashGet } from "lodash-es";

import { Dialog } from "@certa/catalyst/components/Dialog";
import { Typography } from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { toCapital } from "@certa/common/utils/string";

import { ObjectTypeHierarchyStepper } from "./ObjectTypeHierarchyStepper";
import { ObjectTypeHierarchyNavigator } from "./ObjectTypeHierarchyNavigator";
import { useStartWorkflowFromObjectHeirarchy } from "./hooks/useStartWorkflowFromObjectHeirarchy";

import { type Kind } from "@certa/types";
import type { CreateDataObjectWorkflowRelationActionsModelReturn } from "@certa/queries/types/dataObjectsAPIs.types";
import { useIntl } from "react-intl";
import { useObjectTypeTranslationsManager } from "@certa/common/store/dataObjects/useObjectTypeTranslationsManager";
import { DataObjectTable } from "../../dataObjects/src/components/Common/DataObjectTable/DataObjectTable.tsx";
import { Spinner3 } from "@certa/icons/components/Spinner3";
import type { DataObjectTableData } from "../../dataObjects/src/components/Common/DataObjectTable/DataObjectTable.types.ts";

type StartWorkflowFromObjectDialogProps = {
  workflowKind: Kind;
  dataObjectWorkflowRelations: CreateDataObjectWorkflowRelationActionsModelReturn;
  onCreateWorkflow: (kind: Kind, objectId?: string) => void;
  onClose: () => void;
  preSelectedObjectHierarchy?: {
    objectTypeTag: string;
    objectInstanceId: string;
    objectInstanceUid: string;
  }[];
};

export const StartWorkflowFromObjectContent = (
  props: StartWorkflowFromObjectDialogProps
) => {
  const {
    dataObjectWorkflowRelations,
    onCreateWorkflow,
    onClose,
    preSelectedObjectHierarchy,
    workflowKind
  } = props;

  const [dataObjectTableData, setDataObjectTableData] =
    useState<DataObjectTableData>({
      objectDataRowsLength: 0,
      isLoading: false,
      currentObjectTag: "",
      currentObjectTypeDisplayNameSingular: ""
    });

  const {
    initializerObjectHierarchy,
    previousObjectTypeHierarchy,
    currentObjectTypeInitializerHierarchy,
    objectTypesRelationName,
    hasNext,
    hasPrevious,
    onNext,
    onPrevious,
    onSelectObjectInstanceIdHierarchy,
    currentSelectedObjectInstanceId,
    previousSelectedObjectInstanceUid,
    currentObjectTypeWorkflowRelation,
    onCreateObjectWorkflow,
    canShowAddSupplierButton
  } = useStartWorkflowFromObjectHeirarchy({
    dataObjectWorkflowRelations,
    onCreateWorkflow,
    preSelectedObjectHierarchy
  });

  const allowedStatusTags = useMemo(() => {
    const objectWorkflowRelation = dataObjectWorkflowRelations?.find(
      relation => relation.workflowKindTag === workflowKind.tag
    );

    return objectWorkflowRelation?.allowedStatusTags;
  }, [dataObjectWorkflowRelations, workflowKind]);

  const currentObjectTypeTag = currentObjectTypeInitializerHierarchy.tag;

  const intl = useIntl();

  const { getTranslatedUISchema } = useObjectTypeTranslationsManager();
  const objectTypeTranslatedUISchema =
    getTranslatedUISchema(currentObjectTypeTag);

  const objectTypeName =
    lodashGet(objectTypeTranslatedUISchema, "display_names.singular") ||
    dataObjectTableData?.currentObjectTypeDisplayNameSingular ||
    toCapital(dataObjectTableData?.currentObjectTag || currentObjectTypeTag);

  const onSelectObjectInstance = (
    selectedRows: string[],
    selectedRowsData: Record<string, any>[]
  ) => {
    onSelectObjectInstanceIdHierarchy(selectedRows[0], selectedRowsData[0].uid);
  };

  const selectedObjectInstanceRows = currentSelectedObjectInstanceId
    ? { [currentSelectedObjectInstanceId]: true }
    : {};

  const handleNext = () => {
    onNext();
  };

  const handlePrevious = () => {
    onPrevious();
  };

  const selectWithObjectLabel = intl.formatMessage(
    {
      id: "commonTextInstances.selectWithObject",
      defaultMessage: "Select {objectName}"
    },
    {
      objectName: objectTypeName
    }
  );

  const searchForWithObjectLabel = intl.formatMessage(
    {
      id: "commonTextInstances.searchForWithObject",
      defaultMessage: "Search for {objectName}"
    },
    {
      objectName: objectTypeName
    }
  );

  const newObjectLabel = intl.formatMessage(
    {
      id: "commonTextInstances.newObject",
      defaultMessage: "New {objectName}"
    },
    {
      objectName: objectTypeName
    }
  );

  const listLabel = intl.formatMessage({
    id: "commonTextInstances.list",
    defaultMessage: "List"
  });

  return (
    <Stack
      direction="vertical"
      gap="20"
      padding="0 24 16 24"
      height="100%"
      overflow="auto"
    >
      <Stack gap="12" height="fit-content">
        <ObjectTypeHierarchyStepper
          objectTypesHierarchy={initializerObjectHierarchy}
          selectedObjectTypeTag={currentObjectTypeInitializerHierarchy}
        />
        <Typography>{selectWithObjectLabel}</Typography>
      </Stack>
      <Stack height="100%" overflow="auto">
        <DataObjectTable
          allowedStatusTags={
            allowedStatusTags?.length ? allowedStatusTags : undefined // If allowedStatusTags is empty, show all options
          }
          objectType={
            previousSelectedObjectInstanceUid && previousObjectTypeHierarchy
              ? previousObjectTypeHierarchy.tag
              : currentObjectTypeTag
          }
          outgoingObjectId={previousSelectedObjectInstanceUid}
          relationName={
            previousSelectedObjectInstanceUid && previousObjectTypeHierarchy
              ? objectTypesRelationName
              : undefined
          }
          onDataChange={setDataObjectTableData}
          searchInputProps={{
            label: "search",
            placeholder: searchForWithObjectLabel,
            leftIcon: undefined,
            rightIcon: dataObjectTableData.isLoading ? <Spinner3 /> : undefined,
            width: "100%"
          }}
          tableProps={{
            caption: `${objectTypeName} ${listLabel}`,
            height: "40vh",
            rowSelection: {
              enabled: true,
              selectedRows: selectedObjectInstanceRows,
              onChange: onSelectObjectInstance,
              isMultiple: false
            },
            emptyAction: canShowAddSupplierButton
              ? {
                  text: newObjectLabel,
                  onClick: () => onCreateObjectWorkflow("createObject"),
                  disabled: dataObjectTableData.isLoading,
                  loading: dataObjectTableData.isLoading
                }
              : undefined
          }}
          inputContainerProps={{
            gap: "0",
            padding: "4 4 4 4"
          }}
        />
      </Stack>
      <ObjectTypeHierarchyNavigator
        hasSelectedObjectInstance={!!currentSelectedObjectInstanceId}
        objectTypeName={objectTypeName}
        shouldHideCreateObjectButton={
          dataObjectTableData.objectDataRowsLength === 0 ||
          !canShowAddSupplierButton
        }
        shouldShowRedirectLinkAndText={!canShowAddSupplierButton}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onCancel={onClose}
        onCreateObjectWorkflow={onCreateObjectWorkflow}
        currentObjectTypeWorkflowRelation={currentObjectTypeWorkflowRelation}
      />
    </Stack>
  );
};

export const StartWorkflowFromObjectDialog = (
  props: StartWorkflowFromObjectDialogProps
) => {
  const { workflowKind, onClose } = props;

  return (
    <Dialog
      show={!!workflowKind}
      title={workflowKind?.name}
      onClose={onClose}
      preventCloseOnClickOutside={false}
      width="70%"
      noContentStyling
      data-testid="start-workflow-from-object-dialog"
    >
      <StartWorkflowFromObjectContent {...props} />
    </Dialog>
  );
};
